/**
 * Pure utility functions for user name formatting.
 *
 * These functions handle Dutch name conventions, including the "tussenvoegsel"
 * (prefix like "van", "de", "van der") that appears between first and last names.
 *
 * @example
 * ```typescript
 * import { getFullName, getInitials } from '@/utils/userFormatting'
 *
 * const user = { firstName: 'Jan', prefix: 'van der', lastName: 'Berg' }
 * getFullName(user) // "Jan van der Berg"
 * getInitials(user) // "JB"
 * ```
 */

/**
 * Minimal interface for objects that can be formatted as user names.
 * Compatible with UserProfile, EnrichedUserProfile, and similar types.
 */
export interface UserLike {
  firstName: string
  lastName: string
  prefix?: string | null
}

/**
 * Get the full name of a user, including optional prefix (tussenvoegsel).
 *
 * Dutch naming conventions place the "tussenvoegsel" between the first name
 * and last name: "Jan van der Berg", "Pieter de Groot".
 *
 * @param user - Object containing firstName, optional prefix, and lastName
 * @returns Full name string with parts joined by spaces
 *
 * @example
 * ```typescript
 * getFullName({ firstName: 'Jan', lastName: 'Jansen' })
 * // "Jan Jansen"
 *
 * getFullName({ firstName: 'Jan', prefix: 'van', lastName: 'Berg' })
 * // "Jan van Berg"
 *
 * getFullName({ firstName: 'Jan', prefix: 'van der', lastName: 'Berg' })
 * // "Jan van der Berg"
 *
 * getFullName({ firstName: 'Jan', prefix: null, lastName: 'Berg' })
 * // "Jan Berg"
 * ```
 */
export function getFullName(user: UserLike): string {
  const parts = [user.firstName]
  if (user.prefix) {
    parts.push(user.prefix)
  }
  parts.push(user.lastName)
  return parts.join(' ')
}

/**
 * Order users by the name they are displayed under.
 *
 * A picker that lists `getFullName(user)` has to be ordered by that same string.
 * Ordering by `lastName` while showing "Voornaam [tussenvoegsel] Achternaam" reads
 * as unsorted to anyone scanning the list by its first word — and asking the API
 * to sort on `fullName` is not an option either, since no endpoint declares it as
 * a sortable field and the request silently falls back to `lastName` (#4150).
 *
 * @param users - Users to order; the input array is not mutated
 * @returns A new array ordered by full name, using Dutch collation
 *
 * @example
 * ```typescript
 * sortByFullName([
 *   { firstName: 'Bram', lastName: 'Aalders' },
 *   { firstName: 'Anna', prefix: 'de', lastName: 'Zwart' },
 * ])
 * // [{ Anna de Zwart }, { Bram Aalders }]
 * ```
 */
export function sortByFullName<T extends UserLike>(users: readonly T[]): T[] {
  return [...users].sort((a, b) => getFullName(a).localeCompare(getFullName(b), 'nl'))
}

/**
 * Get the initials of a user (first letter of first name + first letter of last name).
 *
 * The prefix (tussenvoegsel) is not included in the initials, following Dutch conventions.
 *
 * @param user - Object containing firstName and lastName
 * @returns Two-letter uppercase initials, or fallback for edge cases
 *
 * @example
 * ```typescript
 * getInitials({ firstName: 'Jan', lastName: 'Jansen' })
 * // "JJ"
 *
 * getInitials({ firstName: 'Jan', prefix: 'van der', lastName: 'Berg' })
 * // "JB"
 *
 * getInitials({ firstName: '', lastName: 'Berg' })
 * // "?B"
 * ```
 */
export function getInitials(user: UserLike): string {
  const firstInitial = user.firstName.charAt(0) || '?'
  const lastInitial = user.lastName.charAt(0) || '?'
  return `${firstInitial}${lastInitial}`.toUpperCase()
}
